/**
 * Unified column mapping for keyword and domain CSV data
 *
 * Flow:
 * 1. API returns cryptic codes (Ph, Nq, Kd, etc.) - semicolon-delimited
 * 2. Backend transforms to readable names (keyword, searchVolume, keywordDifficulty, etc.)
 * 3. Frontend receives transformed CSV with readable headers
 * 4. rowsToObjects() lowercases all headers (searchVolume -> searchvolume)
 *
 * This mapping provides all possible column name variations for robust access.
 */

export interface ColumnMapping {
  // Primary: lowercase transformed name (what rowsToObjects creates)
  primary: string[]
  // Fallback: original cryptic codes (in case transformation didn't happen)
  fallback: string[]
  // Alternative: camelCase, kebab-case, etc.
  alternatives: string[]
}

/**
 * Column mappings for each field type
 * Ordered by priority: most likely to least likely
 */
export const CSV_COLUMN_MAPPINGS: Record<string, ColumnMapping> = {
  keyword: {
    primary: ['keyword', 'ph'],
    fallback: ['ph'],
    alternatives: [],
  },
  searchVolume: {
    primary: ['searchvolume', 'nq'],
    fallback: ['nq'],
    alternatives: ['volume', 'search volume'],
  },
  keywordDifficulty: {
    primary: ['keyworddifficulty', 'kd'],
    fallback: ['kd'],
    alternatives: ['difficulty', 'keyword difficulty'],
  },
  costPerClick: {
    primary: ['costperclick', 'cp'],
    fallback: ['cp'],
    alternatives: ['cpc', 'cost per click'],
  },
  competition: {
    primary: ['competition', 'co'],
    fallback: ['co'],
    alternatives: [],
  },
  searchIntent: {
    primary: ['searchintent', 'in'],
    fallback: ['in'],
    alternatives: ['intent'],
  },
  position: {
    primary: ['position', 'po'],
    fallback: ['po'],
    alternatives: ['pos'],
  },
  previousPosition: {
    primary: ['previousposition', 'pp'],
    fallback: ['pp'],
    alternatives: ['previous position'],
  },
  positionDifference: {
    primary: ['positiondifference', 'pd'],
    fallback: ['pd'],
    alternatives: ['position difference'],
  },
  url: {
    primary: ['url', 'ur'],
    fallback: ['ur'],
    alternatives: [],
  },
  domain: {
    primary: ['domain', 'dn'],
    fallback: ['dn'],
    alternatives: [],
  },
  trafficPercent: {
    primary: ['trafficpercent', 'tr'],
    fallback: ['tr'],
    alternatives: ['traffic', 'traffic %', 'traffic percent'],
  },
  trafficCostPercent: {
    primary: ['trafficcostpercent', 'tc'],
    fallback: ['tc'],
    alternatives: ['traffic cost', 'traffic cost percent'],
  },
  numberOfResults: {
    primary: ['numberofresults', 'nr'],
    fallback: ['nr'],
    alternatives: ['number of results', 'results'],
  },
  trends: {
    primary: ['trends', 'td'],
    fallback: ['td'],
    alternatives: [],
  },
  relevanceScore: {
    primary: ['relevancescore', 'rr'],
    fallback: ['rr'],
    alternatives: ['relevance score', 'relevance'],
  },
  serpFeatures: {
    primary: ['serpfeatures', 'fk'],
    fallback: ['fk'],
    alternatives: ['serp features'],
  },
  opportunityScore: {
    primary: ['opportunityscore'],
    fallback: [],
    alternatives: ['opportunity score'],
  },
}

/**
 * Get column value from row object, trying all possible column name variations
 */
export function getColumnValue(
  row: Record<string, string>,
  fieldName: keyof typeof CSV_COLUMN_MAPPINGS
): string | undefined {
  const mapping = CSV_COLUMN_MAPPINGS[fieldName]
  if (!mapping) return undefined

  // Try primary names first (most likely)
  for (const name of mapping.primary) {
    if (row[name] !== undefined && row[name] !== '') {
      return row[name]
    }
  }

  // Try fallback names
  for (const name of mapping.fallback) {
    if (row[name] !== undefined && row[name] !== '') {
      return row[name]
    }
  }

  // Try alternatives
  for (const name of mapping.alternatives) {
    if (row[name] !== undefined && row[name] !== '') {
      return row[name]
    }
  }

  return undefined
}

/**
 * Get all possible column names for a field (useful for debugging)
 */
export function getColumnNames(fieldName: keyof typeof CSV_COLUMN_MAPPINGS): string[] {
  const mapping = CSV_COLUMN_MAPPINGS[fieldName]
  if (!mapping) return []
  return [...mapping.primary, ...mapping.fallback, ...mapping.alternatives]
}
